/**
 * LÉXICO DE SUPERFÍCIE — fonte única das palavras que o usuário lê (R-UX01 §23/§54).
 *
 * ## Por que este arquivo existe
 *
 * Antes da R-UX01 as etiquetas de status viviam duplicadas em `apps/web/src/lib/copy.ts` e
 * `apps/signer-ui/src/lib/format.ts`. Como toda duplicata sem guarda, elas já tinham
 * divergido: o mesmo `ready` era "Pronto para disparo" no app do dono e "Pronto para
 * iniciar" no do signatário. É exatamente o que §54 proíbe — "não deixar backend e UI
 * usarem palavras diferentes para a mesma condição sem mapper explícito". Este módulo é
 * esse mapper.
 *
 * ## A fronteira: contrato ≠ superfície
 *
 * O §23 pede tirar "envelope" da superfície. Banco, Prisma, rotas `/web/envelopes` e os
 * tipos de `@argink/shared` continuam dizendo `envelope` — são CONTRATO, e quebrá-los não
 * era escopo desta rodada. O que muda é só o que o usuário lê. Por isso o mapa vive aqui,
 * traduzindo a chave técnica para a palavra humana num lugar só.
 *
 * ## Perspectiva não é divergência
 *
 * Status de participante é o único caso em que os dois apps devem mesmo dizer coisas
 * diferentes: o dono lê sobre OUTRA pessoa ("Aguardando convite"), o signatário lê sobre SI
 * MESMO ("Aguardando sua vez"). Isso é audiência, não inconsistência — então as duas
 * perspectivas ficam nomeadas e explícitas aqui, lado a lado, em vez de emergirem por
 * acidente em dois arquivos que ninguém compara. Status de ENVIO não tem perspectiva: é
 * estado do objeto, e é idêntico nos dois apps.
 *
 * ## Browser-safe
 *
 * Este módulo é consumido pelos dois SPAs, então NÃO entra em `index.ts` (que reexporta
 * `webhook-hmac` e arrastaria `node:crypto` para o bundle — DI2-1). Ele é exposto pelo
 * subpath `@argink/shared/surface-lexicon` e importa apenas tipos de `enums.ts`, que é puro.
 */

import type { DocumentFormat, EnvelopeStatus, SignatureLevel, SignerStatus } from './enums';

// ------------------------------------------------------------------ vocabulário do domínio

/**
 * As palavras canônicas do produto (§23). `envelope` NÃO está aqui de propósito: na
 * superfície ele se chama "envio".
 */
export const TERM = {
  /** O arquivo que será assinado. */
  document: 'Documento',
  /** O objeto que agrupa documento + participantes. Substitui "envelope" na superfície. */
  send: 'Envio',
  /** Forma longa — só na primeira menção de um contexto, depois use `send`. */
  sendLong: 'Envio para assinatura',
  /** A ação de criar um envio. É CTA, não nome de objeto. */
  newSend: 'Novo envio',
  /** Quem participa do envio (assina ou observa). */
  participant: 'Participante',
  /** Quem assina. */
  signer: 'Signatário',
} as const;

// ------------------------------------------------------------------ status do envio (7)

/**
 * Estado do envio — SEM perspectiva, idêntico nos dois apps.
 *
 * Duas mudanças em relação ao que estava no ar, ambas do §23:
 *  - `ready`: "Pronto para disparo"/"Pronto para iniciar" → "Pronto para envio". "Disparo"
 *    é jargão de operação, não palavra de quem envia um contrato.
 *  - `in_progress`: "Em andamento" → "Aguardando assinatura". "Em andamento" descreve o
 *    sistema; "aguardando assinatura" responde a pergunta que o usuário tem de verdade,
 *    que é de quem o processo está esperando.
 */
export const ENVELOPE_STATUS_LABEL: Record<EnvelopeStatus, string> = {
  draft: 'Rascunho',
  ready: 'Pronto para envio',
  in_progress: 'Aguardando assinatura',
  completed: 'Concluído',
  declined: 'Recusado',
  expired: 'Expirado',
  cancelled: 'Cancelado',
};

/** Uma frase que explica o estado sem expor mecânica interna. */
export const ENVELOPE_STATUS_DESCRIPTION: Record<EnvelopeStatus, string> = {
  draft: 'Ainda não foi enviado — nenhum convite saiu.',
  ready: 'Participantes e prazo definidos, aguardando o envio.',
  in_progress: 'Convites enviados — aguardando as assinaturas.',
  /* NÃO diz "todos assinaram" (P1-1 da auditoria adversarial do M6). O backend conclui um
   * envelope `parallel_quorum` quando M de N assinam — `computeEnvelopeStatusAfterSign` em
   * `apps/api/src/domain/envelope-state-machine.ts` —, e nesse caso "todos" é literalmente
   * falso, com participantes pendentes ainda listados na mesma tela. O web não OFERECE
   * quorum hoje, mas lê envelopes criados por `/v1`, que oferece: a UI precisa ser verdadeira
   * sobre o dado que recebe, não sobre o dado que ela mesma sabe criar. */
  completed: 'O envio foi concluído. O documento final está disponível.',
  declined: 'Um participante recusou assinar e o envio foi encerrado.',
  expired: 'O prazo terminou antes de todos assinarem.',
  cancelled: 'Cancelado pelo responsável antes da conclusão.',
};

// ------------------------------------------------------------------ status de participante (6)

/**
 * Perspectiva do DONO — ele lê sobre outra pessoa, em terceira pessoa.
 */
export const PARTICIPANT_STATUS_LABEL_OWNER: Record<SignerStatus, string> = {
  pending: 'Aguardando convite',
  notified: 'Convite enviado',
  in_session: 'Assinando agora',
  signed: 'Assinado',
  declined: 'Recusou',
  expired: 'Não assinou a tempo',
};

/**
 * Perspectiva do SIGNATÁRIO — ele lê sobre si mesmo, em segunda pessoa. Não é divergência
 * do mapa acima: é a mesma condição dita para outra audiência, e por isso está aqui do lado,
 * onde a diferença é visível e intencional.
 */
export const PARTICIPANT_STATUS_LABEL_SIGNER: Record<SignerStatus, string> = {
  pending: 'Aguardando sua vez',
  notified: 'Pronto para assinar',
  in_session: 'Sessão aberta',
  signed: 'Assinado',
  declined: 'Recusado',
  expired: 'Prazo encerrado',
};

// ------------------------------------------------------------------ formato do documento

/**
 * Como o produto NOMEIA o formato do documento na superfície — recuperado de uma sessão que
 * nunca chegou a `main` (M6-A, 01/09, `e5f7701` em `vps-sessao-F`); o achado original:
 *
 * O valor de contrato (`pades`/`cades`/`xades`) chegava CRU na tela do signatário —
 * `DocumentCard` renderizava `doc.format.toUpperCase()` e o usuário lia **"PADES"**, que é
 * literalmente um dos termos de `FORBIDDEN_SURFACE_TERMS` logo abaixo. Não é jargão que alguém
 * escreveu à mão — é jargão que VAZOU do contrato por falta de mapper, e nenhum gate de
 * literais no fonte pega isso (o valor nasce no runtime, não em texto estático).
 *
 * `pades` é o perfil de assinatura, não o tipo do arquivo: quem assina abriu um PDF e é isso
 * que a tela deve dizer, mesmo onde jargão de cripto é tolerado (M6-C moveu este campo para
 * "Detalhes técnicos", que aceita os termos de `FORBIDDEN_SURFACE_TERMS` por decisão — mas
 * "aceito" não é "preferível": o rótulo amigável é mais honesto sobre o ARQUIVO em qualquer
 * lugar da tela). `cades` e `xades` não são oferecidos no MVP e estão aqui pelo mesmo motivo
 * que `avancada` está em `MODALITY_DESCRIPTION` — um valor que PODE chegar precisa de rótulo
 * honesto, e um `Record` completo obriga o compilador a cobrar rótulo se o enum crescer.
 */
export const DOCUMENT_FORMAT_LABEL: Record<DocumentFormat, string> = {
  pades: 'PDF',
  cades: 'Arquivo assinado',
  xades: 'XML',
};

// ------------------------------------------------------------------ ações (§23)

// ------------------------------------------------------------------ modalidade de assinatura

/**
 * Como o produto NOMEIA cada modalidade. Estava duplicado — `MODALITY_LABEL`/
 * `MODALITY_DESCRIPTION` em `apps/web/src/lib/copy.ts` e `MODALITY_COPY` em
 * `apps/signer-ui/src/lib/format.ts` — com as MESMAS strings escritas duas vezes e nada
 * comparando as duas cópias. É a mesma armadilha que este arquivo já tinha desarmado para o
 * status do envio (ver o cabeçalho: `ready` chegou a ser "Pronto para disparo" num app e
 * "Pronto para iniciar" no outro). Duplicata que hoje coincide não é duplicata inofensiva:
 * é a que ninguém percebe divergir, porque nada falha no dia em que uma das duas muda.
 *
 * Modalidade NÃO tem perspectiva: é propriedade do documento, idêntica para quem envia e
 * para quem assina.
 */
export const MODALITY_LABEL: Record<SignatureLevel, string> = {
  simples: 'Assinatura Simples',
  avancada: 'Assinatura Avançada',
  qualificada: 'Assinatura Qualificada',
};

/**
 * `avancada` existe aqui porque pode CHEGAR num envelope legado criado por `/v1` e um valor
 * que chega precisa de rótulo honesto — nunca é OFERECIDA na criação (o wizard percorre
 * `SIGNATURE_MODALITIES`, não este mapa).
 */
export const MODALITY_DESCRIPTION: Record<SignatureLevel, string> = {
  simples: 'Verificação por e-mail e registro da manifestação',
  avancada: 'Nível registrado neste documento — não disponível para novos documentos',
  qualificada: 'Certificado digital ICP-Brasil',
};

export const ACTION = {
  sign: 'Assinar',
  signQualified: 'Assinar com certificado digital',
  resendInvite: 'Reenviar convite',
  viewEvidence: 'Ver evidências',
  downloadDocument: 'Baixar documento',
  copyLink: 'Copiar link',
  send: 'Enviar para assinatura',
} as const;

// ------------------------------------------------------------------ jargão barrado (§23)

/**
 * Termos que NÃO podem aparecer na superfície comum. Eles continuam válidos — e corretos —
 * dentro de "Detalhes técnicos", "Segurança", "Evidências" e "Diagnóstico", que é onde quem
 * investiga vai procurar. O que o §23 proíbe é o usuário comum esbarrar neles no caminho de
 * enviar ou assinar um documento.
 *
 * Esta lista existe para que os DOIS SPAs guardem a mesma coisa sem manter cópias que
 * envelhecem sozinhas. Ela nasceu afirmando que o `apps/signer-ui` "já guardava isso com um
 * teste próprio" — e não guardava: o teste citado (`__tests__/no-forbidden-terms.test.tsx`)
 * guarda campo de segredo e `avancada` como opção, nunca jargão. O app do dono tinha gate e
 * estava limpo; o do signatário não tinha e já havia divergido — "Envelope expirado" no 410 e
 * "sem nonce de autorização" chegando à tela pelo `err.message`. Um docblock que descreve uma
 * proteção inexistente é pior que a ausência dela, porque encerra a pergunta.
 *
 * Consumidores REAIS, hoje: `apps/web/test/rtl/no-forbidden-terms.test.tsx` e
 * `apps/signer-ui/src/__tests__/surface-lexicon.test.ts`.
 *
 * "Cerimônia", "participante", "etapa", "evidência" e "consentimento" NÃO estão aqui: são
 * termos PT deliberados, aceitos por decisão anterior de Sol+Terra
 * (`.claude/pre-hosted-baseline.md`, Fase 2), e a R-UX01 não reabriu isso.
 */
export const FORBIDDEN_SURFACE_TERMS: readonly string[] = [
  'provider',
  'credential',
  'local agent',
  'nonce',
  'challenge',
  'ledger',
  'Merkle',
  'LCR',
  'OCSP',
  'PKCS#11',
  'CSP',
  'KSP',
  'PAdES',
];
